/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import {DestroyRef, Injectable} from '@angular/core';
import {SpatialUtilService} from '../spatial-util.service';
import {EsriModuleService} from '../../esri-nlr/services/esri-module.service';
import {FeatureApplyUpdateService} from '../feature-apply-update.service';
import {PlanModelService} from '../../../common/service/plan-model.service';
import {catchError, map, of, tap} from 'rxjs';
import {SpatialSequenceService} from '../../../common/service/spatial-sequence.service';
import {FeaturesApplyInsertService} from '../features-apply-insert.service';
import {FeatureAssociationService} from './feature-association.service';
import {AnnotationService} from './annotation.service';
import {AngleUtilService} from './angle/angle-util.service';
import {MapModeService} from '../map-mode.service';
import {PrintAreaService} from '../../../common/service/print-area.service';
import {MetaData, NgEventBus} from 'ng-event-bus';
import {switchMap} from 'rxjs/operators';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {equals} from '../../../common/service/data-util';
// eslint-disable-next-line max-len
import {TenureInterestsNotificationModalService} from '../../../common/component/modal/tenure-interests-notification-modal/tenure-interests-notification-modal.service';
// eslint-disable-next-line max-len
import {ManualFormerTenureInterestNotificationModalService} from '../../../common/component/modal/manual-former-tenure-interest-notification-modal/manual-former-tenure-interest-notification-modal.service';
import {AlertModalService} from '../../../common/component/modal';
// eslint-disable-next-line max-len
import {NewInterestNotificationModalService} from '../../../common/component/modal/new-interests-notification-modal/new-interest-notification-modal.service';
import {PlanLodgementService} from '../../../common/service/plan-lodgement.service';

@Injectable({
    providedIn: 'root'
})
export class InterestService {
    title = 'InterestService';

    handler: any;

    constructor(
        private alertModalService: AlertModalService,
        private angleUtilService: AngleUtilService,
        private annotationService: AnnotationService,
        private esriModuleService: EsriModuleService,
        private destroyRef: DestroyRef,
        private eventBus: NgEventBus,
        private featuresApplyInsertService: FeaturesApplyInsertService,
        private featureApplyUpdateService: FeatureApplyUpdateService,
        private featureAssociationService: FeatureAssociationService,
        private mapModeService: MapModeService,
        private newInterestNotificationModalService: NewInterestNotificationModalService,
        private planModelService: PlanModelService,
        private planLodgementService: PlanLodgementService,
        private printAreaService: PrintAreaService,
        private spatialSequenceService: SpatialSequenceService,
        private spatialUtilService: SpatialUtilService,
        private tenureInterestsNotificationModalService: TenureInterestsNotificationModalService,
        private manualFormerTenureInterestNotificationModalService: ManualFormerTenureInterestNotificationModalService
    ) {
        this.eventBus
            .on('removeInterestAnnotationAssociation')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                console.debug(`${this.title} - on - removeInterestAnnotationAssociation`);
                this.deleteHandler();
            });

        this.eventBus
            .on('addInterestAnnotationAssociation')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((meta: MetaData) => {
                console.debug(`${this.title} - on - addInterestAnnotationAssociation`);

                const attrs = meta.data;

                const interest = spatialUtilService.findById('interest', attrs.id);
                const featureSubtype = interest.attributes.polylineids ? 24 : interest.attributes.type;

                const attributes = {
                    featuretype: 'interest',
                    featureid: attrs.id,
                    featuresubtype: featureSubtype,
                    guid: planModelService.getGuid(),
                    rotation: 90.0,
                    rotationdms: '90:00:00'
                };

                const attributesGroup = {};

                annotationService.getAnnotationConfigurations('interest', featureSubtype).forEach(ac => {
                    attributes[ac.annotationAttribute] = attrs[ac.featureAttribute];
                    attributesGroup[ac.annotationAttribute] = !!ac.parent;
                });

                this.create(attributes, attributesGroup);
            });

        this.eventBus
            .on('saveInterestDissociation')
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap((meta: MetaData) => {
                    console.debug(`${this.title} - on - saveInterestDissociation`);
                    const {interest, childId, childType} = meta.data;

                    if (!interest) {
                        return of(null);
                    }

                    const child = this.spatialUtilService.findById(childType, childId);

                    if (child) {
                        const updateMethod =
                            childType === 'polygon'
                                ? this.featureAssociationService.updatePolygonAssociationChanges.bind(this.featureAssociationService)
                                : this.featureAssociationService.updatePolylineAssociationChanges.bind(this.featureAssociationService);
                        return updateMethod(interest, [child], [], 'interestids').pipe(
                            switchMap(() => {
                                this.spatialUtilService.unhighlightFeatures(childType, [child]);

                                return this.featureAssociationService
                                    .updateBusinessFeatureAssociationChanges(
                                        this.spatialUtilService.getInterestMapLayer(),
                                        interest,
                                        [child],
                                        [],
                                        childType
                                    )
                                    .pipe(
                                        tap(() =>
                                            window.setTimeout(() => {
                                                this.eventBus.cast('deleteAnnotations', {
                                                    featureLayer: this.spatialUtilService.getMapFeatureLayer(childType),
                                                    features: [child],
                                                    parentFeatureType: 'interest',
                                                    parentFeatureId: interest.attributes.id
                                                });
                                            }, 500)
                                        )
                                    );
                            })
                        );
                    } else {
                        if (childType === 'polygon') {
                            const polygonIds = spatialUtilService.parseIdsAttr(interest.attributes.polygonids);
                            if (polygonIds.includes(childId)) {
                                interest.attributes.polygonids = spatialUtilService.toIdsAttr(polygonIds.filter(id => id !== childId));
                            }
                        } else {
                            const polylineIds = spatialUtilService.parseIdsAttr(interest.attributes.polylineids);
                            if (polylineIds.includes(childId)) {
                                interest.attributes.polylineids = spatialUtilService.toIdsAttr(polylineIds.filter(id => id !== childId));
                            }
                        }
                        return spatialUtilService.applyEdits('interest', null, [interest], null);
                    }
                }),
                tap(() => {
                    this.eventBus.cast('interestDissociationComplete');
                }),
                catchError(reason => {
                    console.debug(`${this.title} - error:`, reason);
                    return of(null);
                })
            )
            .subscribe();
    }

    addInterests(nonSpatialInterests: any[]) {
        console.debug(`${this.title} - addInterests`);

        const interests = this.spatialSequenceService
            .getInterestSequence(nonSpatialInterests.length)
            .map((v, i) =>
                this.populateAttributes(
                    nonSpatialInterests[i],
                    new this.esriModuleService.Graphic(new this.esriModuleService.geometry.Polygon(), null, {id: v})
                )
            );

        return this.featuresApplyInsertService.insertFeaturesByType(this.spatialUtilService.getInterestMapLayer(), interests).pipe(
            switchMap((createdInterests: any) => {
                console.debug(`${this.title} - addInterests - success`);
                return this.spatialUtilService.save().pipe(map(() => createdInterests));
            })
        );
    }

    updateInterests(nonSpatialInterests: any[]) {
        console.debug(`${this.title} - updateInterests`);

        const allInterests = this.spatialUtilService.getInterests();

        const interestsToUpdate = nonSpatialInterests
            .map(nonSpatialInterest => {
                const interest = allInterests.find((f: any) => f.attributes.interestid === nonSpatialInterest.interestId);

                if (interest && interest.attributes) {
                    const interestToUpdate = this.spatialUtilService.copyGraphic(interest);
                    this.populateAttributes(nonSpatialInterest, interestToUpdate);
                    return interestToUpdate;
                }
            })
            .filter(v => v);

        return this.featureApplyUpdateService
            .updateFeaturesByType(this.spatialUtilService.getInterestMapLayer(), interestsToUpdate)
            .pipe(tap(() => console.debug(`${this.title} - on - updateInterest - Interests successfully updated!`)));
    }

    create(attributes: any, attributesGroup: any) {
        console.debug(`${this.title} - create`);

        this.spatialUtilService.map.setMapCursor(this.spatialUtilService.getCrossCursorUrl());

        this.handler = this.spatialUtilService.map.on('click', (event: any) => {
            this.deleteHandler();
            this.createInterestAnnotation(attributes, attributesGroup, event.mapPoint).subscribe(() => {
                this.eventBus.cast('stopinterestAnnotationTool');
                this.spatialUtilService.map.setMapCursor(this.spatialUtilService.getDefaultCursorUrl());
            });
        });
    }

    /***
     * Deletes all interest geometries (only)
     */
    deleteAllInterestGeometries() {
        console.debug(`${this.title} - deleteAllInterestLotGeometries`);

        // Deletes all interest geometries (used for blue border highlighting)
        const interests = this.spatialUtilService.getInterests();

        if (interests.length) {
            const features = interests.map((interest: any) => {
                interest.geometry = new this.esriModuleService.Polygon();
                return interest;
            });
            this.featureApplyUpdateService
                .updateFeaturesByType(this.spatialUtilService.getInterestMapLayer(), features)
                .subscribe(() => console.debug(`${this.title} - deleteAllInterestLotGeometries - Interest geometries successfully deleted.`));
        }
    }

    /**
     * Updates any interests that are associated with the lots with empty geometries in order to clear the blue border
     * @param lotNames comma delimited list of lot names
     */
    deleteLotInterestGeometries(lotNames: string[]) {
        console.debug(`${this.title} - deleteLotInterestGeometries - lot names:`, lotNames);

        const interests = this.spatialUtilService
            .getInterests()
            .filter((f: any) => f.attributes.burdenlots && f.attributes.burdenlots.split().some((l: any) => lotNames.includes(l)));

        if (interests.length) {
            //  update only the interests that have geometry
            const features = interests
                .filter((interest: any) => interest.geometry?.rings?.length)
                .map((interest: any) => {
                    interest.geometry = new this.esriModuleService.Polygon(interest.geometry.spatialReference);
                    return interest;
                });

            if (features.length) {
                this.featureApplyUpdateService
                    .updateFeaturesByType(this.spatialUtilService.getInterestMapLayer(), features)
                    .subscribe(() => console.debug(`${this.title} - deleteLotInterestGeometries - ${features.length} features updated`));
            }
        }
    }

    /***
     * Updates the interest geometry with the lot feature layer geometry if the burden lots exist.
     * If burden lots aren't found in the lot feature layer: updates with empty geometry
     * DCX-6774
     * @param spatialInterest interest feature
     */
    populateInterestGeometry(spatialInterest: any) {
        console.debug(`${this.title} - populateInterestGeometry`);

        const burdenLotNames = spatialInterest.attributes.burdenlots?.split(',') || [];
        const spatialBurdenLots = this.spatialUtilService
            .getLots()
            .filter((f: any) => f.attributes.name && burdenLotNames.includes(f.attributes.name.toString()));

        const interestPolygonGeometry = new this.esriModuleService.geometry.Polygon();

        if (spatialBurdenLots.length) {
            interestPolygonGeometry.setSpatialReference(spatialBurdenLots[0].geometry.spatialReference);
            spatialBurdenLots.forEach((feature: any) => feature.geometry?.rings?.forEach((ring: any) => interestPolygonGeometry.addRing(ring)));
        }

        //update the interest geometry - empty if no plan lots found:
        spatialInterest.geometry = interestPolygonGeometry;
    }

    /**
     * Updates all interest geometries for the passed business features.
     * DCX-6774
     * @param businessFeatures business features for which to update interest geometries
     */
    updateInterestGeometry(businessFeatures: any[]) {
        const logPrefix = `${this.title} - updateInterestGeometry`;
        if (businessFeatures.length) {
            console.debug(logPrefix);

            const features = [];

            businessFeatures.forEach((businessFeature: any) => {
                const feature = this.spatialUtilService.getInterests().find((f: any) => f.attributes.interestid === businessFeature.interestId);

                if (feature) {
                    this.populateInterestGeometry(feature);
                    features.push(feature);
                }
            });

            if (features.length) {
                return this.spatialUtilService.applyEdits('interest', null, features, null).pipe(
                    tap((res: any) => {
                        console.debug(`${logPrefix} - updates:`, res.updates);
                        this.spatialUtilService.redrawFeatureLayer('interest');
                    })
                );
            } else {
                console.warn(`${logPrefix} - no features to update`);
            }
        } else {
            console.debug(`${logPrefix} - no features passed`);
        }
        return of(null);
    }

    updateInterestsWithNoGeometry(businessFeatures: any, callback?: any) {
        console.debug(this.title + ' : updateInterestsWithNoGeometry');
        if (businessFeatures.length > 0) {
            const features = [];

            businessFeatures.forEach(businessFeature => {
                const feature = this.spatialUtilService.getInterests().find(f => f.attributes.interestid === businessFeature.interestId);

                if (feature) {
                    if (!feature.geometry || feature.geometry.rings.length === 0) {
                        this.populateAttributes(businessFeature, feature);
                        features.push(feature);
                    }
                }
            });

            if (features.length > 0) {
                this.featureApplyUpdateService.updateFeaturesByType(this.spatialUtilService.getInterestMapLayer(), features, updatedFeatures => {
                    if (callback) {
                        callback(updatedFeatures);
                    }
                });
            }
        }
    }

    populateAttributes(nonSpatialInterest: any, spatialInterest: any) {
        console.debug(this.title + ' : populateAttributes');

        spatialInterest.attributes.guid = this.planModelService.getGuid();
        spatialInterest.attributes.interestid = nonSpatialInterest.interestId;
        spatialInterest.attributes.type = nonSpatialInterest.type;
        spatialInterest.attributes.subject = nonSpatialInterest.subject || null;
        spatialInterest.attributes.description = nonSpatialInterest.description;
        spatialInterest.attributes.tenure = nonSpatialInterest.tenure;
        spatialInterest.attributes.burdenlots = nonSpatialInterest.burdenLots.join(',');
        spatialInterest.attributes.beneficiarylots = nonSpatialInterest.beneficiaryLots.join(',');
        spatialInterest.attributes.interestcode = nonSpatialInterest.interestcode;
        spatialInterest.attributes.portion = nonSpatialInterest.portion || null;

        this.populateInterestGeometry(spatialInterest);

        return spatialInterest;
    }

    /***
     * Updates any interests that are associated with the lots.
     * DCX-8067
     * @param lotNames list of lots to update
     */
    updateLotInterests(lotNames: any[]) {
        console.debug(`${this.title} - updateLotInterests - lotNames:`, lotNames);

        const interestFeatures = this.spatialUtilService
            .getInterests()
            .filter((f: any) => f.attributes.burdenlots && f.attributes.burdenlots.split(',').some((l: string) => lotNames.map(String).includes(l)));

        if (interestFeatures.length) {
            const features = interestFeatures.map((f: any) => {
                const feature = this.spatialUtilService.copyGraphic(f);
                this.populateInterestGeometry(feature);
                return feature;
            });
            this.spatialUtilService
                .applyEdits('interest', null, features, null)
                .pipe(tap(res => console.debug(`${this.title} - updateLotInterests - updates:`, res.updates)));
        }
    }

    findNonSpatialInterest(spatialInterest: any, newInterests: any[], formerTenureInterests: any[]) {
        console.debug(`${this.title} - findNonSpatialInterest`);

        if (spatialInterest.attributes.type === 1) {
            return formerTenureInterests
                .reduce((acc, formerTenure) => {
                    acc.push(...formerTenure.formerTenureNotifications);
                    return acc;
                }, [])
                .find((ftn: any) => ftn.subjects.some((s: any) => s.id === spatialInterest.attributes.interestid));
        }

        return newInterests.find(newInterest => newInterest.id === spatialInterest.attributes.interestid);
    }

    updateInterestIds(currentValue: any, valueToInsert: any, remove: boolean) {
        console.debug(`${this.title} - updateInterestIds`);

        const bracketedValueToInsert = `[${valueToInsert}]`;

        if (!currentValue) {
            return bracketedValueToInsert;
        }

        if (remove) {
            if (currentValue.includes(bracketedValueToInsert)) {
                currentValue = currentValue.replace(bracketedValueToInsert, '');
                currentValue = currentValue.replace(',,', ',');
            }
        } else {
            currentValue = currentValue + `,${bracketedValueToInsert}`;
        }

        return currentValue;
    }

    createInterestAnnotation(attributes: any, attributesGroup: any, mapPoint: any) {
        console.debug(`${this.title} - createInterestAnnotation`);

        let isGroup = !Object.values(attributesGroup).some((v: any) => v === false);

        const baseOffset = this.printAreaService.getReferenceScaleDivisor() * 0.004;

        const annotationsToBeCreated = [];

        for (const [groupKey, groupValue] of Object.entries(attributesGroup) as any[]) {
            if (groupValue === false || isGroup) {
                const attributeNumber = groupKey.replace(/^\D+/g, '');
                const offsetY = isGroup ? 0 : baseOffset * (attributeNumber === '' ? 0 : parseInt(attributeNumber) - 1);

                const pointGeometry = new this.esriModuleService.geometry.Point(mapPoint.x, mapPoint.y - offsetY);
                pointGeometry.setSpatialReference(mapPoint.spatialReference);

                const pointAttributes: {x: number; y: number; enlargementid?: number; rotation?: number; rotationdms?: string} = {
                    x: mapPoint.x,
                    y: mapPoint.y
                };

                for (const [attributeKey, attribute] of Object.entries(attributes) as any[]) {
                    if (attribute && typeof attribute === 'object') {
                        pointAttributes[attributeKey] = attribute.id;
                    } else {
                        if (attributesGroup[attributeKey] || attributeKey == groupKey || typeof attributesGroup[attributeKey] === 'undefined') {
                            pointAttributes[attributeKey] = attribute;
                        }
                    }
                }

                if (this.mapModeService.isMapModeType('enlargement')) {
                    pointAttributes.enlargementid = this.mapModeService.mapMode.id;
                }

                const pointGraphic = new this.esriModuleService.Graphic(pointGeometry);
                if (pointAttributes.rotationdms) {
                    pointAttributes.rotation = this.angleUtilService.dmsToDd(pointAttributes.rotationdms);
                }
                pointGraphic.attributes = pointAttributes;
                annotationsToBeCreated.push(pointGraphic);

                isGroup = false;
            }
        }

        return this.annotationService.populateLabelExpressionAndCreateAnnotations(annotationsToBeCreated);
    }

    deleteInterests(interests: any[]) {
        const logPrefix = `${this.title} - deleteInterests`;
        console.info(logPrefix);

        if (interests.length === 0) {
            return of(null);
        }

        const filteredInterests = this.spatialUtilService
            .getInterests()
            .filter((f: any) => interests.some(i => i.interestid === f.attributes.interestid));

        console.debug(`${logPrefix} - filteredInterests:`, filteredInterests);

        if (filteredInterests.length === 0) {
            return of(null);
        }

        const edits: {interest: any[]; annotation?: any[]; line?: any[]; polygon?: any[]} = {
            interest: [null, null, filteredInterests]
        };

        const interestIds: any[] = filteredInterests.map((v: any) => v.attributes.id);

        const interestAnnotations = this.spatialUtilService
            .getAnnotations()
            .filter((f: any) => interestIds.includes(f.attributes.featureid) && f.attributes.featuretype === 'interest');

        console.debug(`${logPrefix} - interestAnnotations:`, interestAnnotations);

        if (interestAnnotations.length) {
            edits.annotation = [null, null, interestAnnotations];
        }

        const interestPolygons = this.spatialUtilService
            .getPolygons()
            .filter((f: any) => interests.some(interest => f.attributes.interestids?.split().includes(`[${interest.interestid}]`)));

        console.debug(`${logPrefix} - interestPolygons:`, interestPolygons);

        if (interestPolygons.length) {
            const polygonUpdates = interestPolygons.map(
                (v: any) =>
                    new this.esriModuleService.Graphic(
                        null,
                        null,
                        Object.assign(structuredClone(v.attributes), {
                            interestids:
                                this.spatialUtilService
                                    .parseIdsAttr(v.attributes.interestids)
                                    .filter(v => !interestIds.includes(v))
                                    .map(v => `[${v}]`)
                                    .join() || null
                        })
                    )
            );
            edits.polygon = [null, polygonUpdates, null];
        }

        const polylines = this.spatialUtilService
            .getPolylines()
            .filter((f: any) => interestIds.some(interestId => this.spatialUtilService.parseIdsAttr(f.attributes.interestids).includes(interestId)));

        if (polylines.length) {
            const lineUpdates = polylines.map(v => {
                return new this.esriModuleService.Graphic(
                    this.spatialUtilService.copyGeometry(v.geometry),
                    null,
                    Object.assign(structuredClone(v.attributes), {
                        interestids:
                            this.spatialUtilService
                                .parseIdsAttr(v.attributes.interestids)
                                .filter(v => !interestIds.includes(v))
                                .map(v => `[${v}]`)
                                .join() || null
                    })
                );
            });
            edits.line = [null, lineUpdates, null];
        }

        if (edits.annotation && edits.annotation[2] && edits.annotation[2].length) {
            const annotationsToDelete = edits.annotation[2];
            const idsOfAnnotationsToDelete = annotationsToDelete.map(v => v.attributes.id);

            const enlargementAnnotationsToDelete = this.spatialUtilService
                .getAnnotations()
                .filter((f: any) => idsOfAnnotationsToDelete.includes(f.attributes.parentid));

            console.debug(`${logPrefix} - enlargementAnnotationsToDelete:`, enlargementAnnotationsToDelete);

            annotationsToDelete.push(...enlargementAnnotationsToDelete);
        }

        let observable = of(null);

        if (edits.annotation) {
            observable = observable.pipe(
                switchMap(() => this.spatialUtilService.applyEdits('annotation', edits.annotation[0], edits.annotation[1], edits.annotation[2])),
                tap(res => {
                    console.debug(`${logPrefix} - annotation - applyEdits response:`, res);
                })
            );
        }

        if (edits.polygon) {
            observable = observable.pipe(
                switchMap(() => this.spatialUtilService.applyEdits('polygon', edits.polygon[0], edits.polygon[1], edits.polygon[2])),
                tap(res => {
                    console.debug(`${logPrefix} - polygon - applyEdits response:`, res);
                })
            );
        }

        if (edits.line) {
            observable = observable.pipe(
                switchMap(() => this.spatialUtilService.applyEdits('line', edits.line[0], edits.line[1], edits.line[2])),
                tap(res => {
                    console.debug(`${logPrefix} - line - applyEdits response:`, res);
                })
            );
        }

        if (edits.interest) {
            observable = observable.pipe(
                switchMap(() => this.spatialUtilService.applyEdits('interest', edits.interest[0], edits.interest[1], edits.interest[2])),
                tap(res => {
                    console.debug(`${logPrefix} - interest - applyEdits response:`, res);
                })
            );
        }

        console.debug(`${logPrefix} - edits:`, edits);

        return observable;
    }

    openTenureInterestsNotificationsModal(item: {subjects: any[]}) {
        console.debug(`${this.title} - openTenureInterestsNotificationsModal`);

        const overwritableSubjectVals = item.subjects
            .filter(subject => subject.subject && subject.subject.length > 0)
            .map(subject => subject.subject);

        return this.tenureInterestsNotificationModalService
            .showModal({tenureInterestNotification: structuredClone(item), overwritableSubjectVals: overwritableSubjectVals})
            .pipe(
                switchMap((newItem: any) => {
                    if (newItem === undefined || newItem === 'Closed') {
                        return of();
                    }
                    if (newItem.subjects.length === 0) {
                        newItem.subjects.push({affectedLots: []});
                    }

                    this.planModelService.getModel().formerTenures.forEach(ft => {
                        const ftnIdx = ft.formerTenureNotifications.findIndex(ftn => equals(ftn, item));
                        if (ftnIdx !== -1) {
                            ft.formerTenureNotifications.splice(ftnIdx, 1, newItem);
                        }
                    });

                    const obs = this.planModelService.save();
                    this.eventBus.cast('nlr:busy', obs.subscribe());
                    return of(null);
                }),
                catchError(reason => this.alertModalService.createAlertMessage(reason))
            );
    }

    openManualFormerTenureInterestNotificationModal(nonDigitalFtns: any[], existingNotification?: any) {
        console.debug(`${this.title} - openManualFormerTenureInterestNotificationModal`);

        return this.manualFormerTenureInterestNotificationModalService
            .showModal({
                formerTenures: structuredClone(nonDigitalFtns),
                newFormerTenureInterestNotification: existingNotification ? structuredClone(existingNotification) : undefined
            })
            .pipe(
                switchMap((newItem: any) => {
                    if (newItem === undefined || newItem === 'Closed') {
                        return of();
                    }

                    if (newItem.subjects.length === 0) {
                        newItem.subjects.push({affectedLots: []});
                    }

                    if (existingNotification) {
                        // Editing: find the FTN by uid and splice-replace
                        this.planModelService.getModel().formerTenures.forEach((ft: any) => {
                            const ftnIdx = ft.formerTenureNotifications.findIndex((ftn: any) => ftn.uid === newItem.uid);
                            if (ftnIdx !== -1) {
                                ft.formerTenureNotifications.splice(ftnIdx, 1, newItem);
                            }
                        });
                    } else {                        
                        this.planModelService.getModel().formerTenures.forEach((ft: any) => {
                            if (ft.id === newItem.formerTenureId) {
                                ft.formerTenureNotifications = ft.formerTenureNotifications ?? [];
                                ft.formerTenureNotifications.push(newItem);
                            }
                        });
                    }

                    const obs = this.planModelService.save();
                    this.eventBus.cast('nlr:busy', obs.subscribe());
                    return of(null);
                }),
                catchError(reason => this.alertModalService.createAlertMessage(reason))
            );
    }

    openNewInterestModal(interest: any) {
        console.debug(`${this.title} - openNewInterestModal`);

        return this.newInterestNotificationModalService.showModal({interest: interest ? structuredClone(interest) : null}).pipe(
            switchMap((newInterest: object | string) => {
                if (newInterest === 'Closed' || newInterest === 'no') {
                    return of(false);
                }
                if (typeof newInterest === 'string') {
                    console.log('Unhandled modal result ' + newInterest);
                    return of(false);
                }

                if (!this.planModelService.getModel().interests) {
                    this.planModelService.getModel().interests = [];
                }

                if (interest) {
                    //Remove existing interest attributes
                    Object.keys(interest).forEach(k => delete interest[k]);
                    //Assign newInterest attributes to interest
                    interest = Object.assign(interest, newInterest);
                } else {
                    this.planModelService.getModel().interests.push(newInterest);
                }

                const obs = this.planLodgementService.save();
                this.eventBus.cast('nlr:busy', obs.subscribe());
                return of(null);
            }),
            tap(() => this.eventBus.cast('initiateNewInterests')),
            catchError(reason => this.alertModalService.createAlertMessage(reason))
        );
    }

    private deleteHandler() {
        if (this.handler) {
            this.handler.remove();
        }
    }
}
